<template>
  <div class="container">
    <h2 class="title">Exams Management</h2>

    <div class="filter-bar">
      <button class="btn add-btn" @click="openCreateModal">Create New Exam</button>
    </div>

    <div class="table-container" v-if="exams.length > 0">
      <table class="modern-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Term</th>
            <th>Class</th>
            <th>Subject</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="exam in exams" :key="exam.id">
            <td>{{ exam.name }}</td>
            <td>{{ exam.type?.name }}</td>
            <td>{{ exam.term?.name }}</td>
            <td>{{ exam.class?.name || '—' }}</td>
            <td>{{ exam.subject?.name || '—' }}</td>
            <td>
              <span :class="['status-badge', exam.is_published ? 'published' : 'draft']">
                {{ exam.is_published ? 'Published' : 'Draft' }}
              </span>
            </td>
            <td>
              <button class="btn edit-btn" @click="editExam(exam)">Edit</button>
              <button class="btn view-btn" @click="viewExam(exam)">View</button>
              <button class="btn delete-btn" @click="deleteExam(exam.id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="no-results">
      <p>No exams found. Create your first exam!</p>
    </div>

    <!-- Create/Edit Exam Modal -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ isEditing ? 'Edit Exam' : 'Create New Exam' }}</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitExam">
            <!-- Basic Info -->
            <div class="form-section">
              <h4>Basic Information</h4>
              <div class="form-group">
                <label>Exam Name</label>
                <input v-model="form.name" type="text" required />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Exam Type</label>
                  <select v-model="form.exam_type_id" required>
                    <option value="" disabled>Select Type</option>
                    <option v-for="type in examTypes" :key="type.id" :value="type.id">
                      {{ type.name }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label>Term</label>
                  <select v-model="form.term_id" required>
                    <option value="" disabled>Select Term</option>
                    <option v-for="term in terms" :key="term.id" :value="term.id">
                      {{ term.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Class (optional)</label>
                  <select v-model="form.class_id">
                    <option value="">Select Class</option>
                    <option v-for="classItem in classes" :key="classItem.id" :value="classItem.id">
                      {{ classItem.name }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label>Subject (optional)</label>
                  <select v-model="form.subject_id">
                    <option value="">Select Subject</option>
                    <option v-for="subject in subjects" :key="subject.id" :value="subject.id">
                      {{ subject.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Timing & Settings -->
            <div class="form-section">
              <h4>Timing & Settings</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Start Time</label>
                  <input v-model="form.start_time" type="datetime-local" />
                </div>

                <div class="form-group">
                  <label>End Time</label>
                  <input v-model="form.end_time" type="datetime-local" />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Duration (minutes)</label>
                  <input v-model="form.duration_minutes" type="number" min="1" />
                </div>

                <div class="form-group">
                  <label>Total Score</label>
                  <input v-model="form.total_score" type="number" min="1" required />
                </div>
              </div>

              <div class="form-group">
                <label>Instructions</label>
                <textarea v-model="form.instructions" rows="3"></textarea>
              </div>

              <div class="form-check">
                <input v-model="form.is_published" type="checkbox" id="is_published" />
                <label for="is_published">Publish Exam</label>
              </div>
            </div>

            <!-- Questions Section -->
            <div class="form-section">
              <h4>Questions</h4>
              <div class="question-list">
                <div v-for="(question, qIndex) in form.questions" :key="qIndex" class="question-card">
                  <div class="question-header">
                    <h5>Question #{{ qIndex + 1 }}</h5>
                    <button type="button" class="btn remove-btn" @click="removeQuestion(qIndex)">
                      Remove
                    </button>
                  </div>

                  <div class="form-group">
                    <label>Question Text</label>
                    <textarea v-model="question.question_text" required rows="3"></textarea>
                  </div>

                  <div class="form-row">
                    <div class="form-group">
                      <label>Question Type</label>
                      <select v-model="question.type" required @change="question.options = []">
                        <option value="multiple_choice">Multiple Choice</option>
                        <option value="essay">Essay</option>
                      </select>
                    </div>

                    <div class="form-group">
                      <label>Points</label>
                      <input v-model="question.points" type="number" min="1" required />
                    </div>
                  </div>


                  <div v-if="question.type === 'essay'" class="form-group">
  <label>Correct Answer (for teacher reference)</label>
  <textarea v-model="question.correct_answer" rows="3"></textarea>
</div>
                  <!-- Options for Multiple Choice -->
                  <div v-if="question.type === 'multiple_choice'" class="options-section">
  <div v-for="(option, oIndex) in question.options" :key="oIndex" class="option-item">
    <div class="option-input">
      <input
        type="radio"
        :name="'q'+qIndex+'_correct'"
        :value="oIndex"
        v-model="question.correct_option"
        @change="updateCorrectOption(qIndex, oIndex)"
      />
      <input
        v-model="option.option_text"
        type="text"
        placeholder="Option text"
        required
      />
    </div>
    <button
      type="button"
      class="btn remove-btn"
      @click="removeOption(qIndex, oIndex)"
    >
      Remove
    </button>
  </div>

  <button
    type="button"
    class="btn add-btn"
    @click="addOption(qIndex)"
  >
    Add Option
  </button>
</div>

                </div>

                <button type="button" class="btn add-btn" @click="addQuestion">
                  Add Question
                </button>
              </div>
            </div>

            <!-- Attachments -->
            <div class="form-section">
              <h4>Attachments</h4>
              <div class="attachments-list">
                <div v-for="(attachment, aIndex) in form.attachments" :key="aIndex" class="attachment-item">
                  <span>{{ attachment.file_name }}</span>
                  <button type="button" class="btn remove-btn" @click="removeAttachment(aIndex)">
                    Remove
                  </button>
                </div>
              </div>

              <div class="file-upload">
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileUpload"
                  multiple
                  style="display: none"
                />
                <button type="button" class="btn" @click="$refs.fileInput.click()">
                  Add Attachment
                </button>
              </div>
            </div>

            <div class="form-actions">
              <button type="button" class="btn cancel-btn" @click="closeModal">
                Cancel
              </button>
              <button type="submit" class="btn submit-btn">
                {{ isEditing ? 'Update' : 'Create' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- View Exam Modal -->
    <div v-if="showViewModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ currentExam.name }}</h3>
          <button class="close-btn" @click="closeViewModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="exam-details">
            <div class="detail-row">
              <span class="detail-label">Type:</span>
              <span>{{ currentExam.type?.name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Term:</span>
              <span>{{ currentExam.term?.name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Class:</span>
              <span>{{ currentExam.class?.name || '—' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Subject:</span>
              <span>{{ currentExam.subject?.name || '—' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status:</span>
              <span :class="['status-badge', currentExam.is_published ? 'published' : 'draft']">
                {{ currentExam.is_published ? 'Published' : 'Draft' }}
              </span>
            </div>
          </div>

          <div class="questions-section">
            <h4>Questions</h4>
            <div v-for="(question, qIndex) in currentExam.questions" :key="qIndex" class="question-view">
              <div class="question-text">
                <strong>Q{{ qIndex + 1 }}:</strong> {{ question.question_text }} ({{ question.points }} points)
              </div>
              
              <div v-if="question.type === 'multiple_choice'" class="options-view">
                <div v-for="(option, oIndex) in question.options" :key="oIndex" class="option-view">
                  <input type="radio" :name="'view_q'+qIndex" disabled :checked="option.is_correct" />
                  <span :class="{ 'correct-option': option.is_correct }">{{ option.option_text }}</span>
                </div>
              </div>
              <div v-else class="essay-view">
  <p>Essay question - student will provide written answer</p>
  <div v-if="question.correct_answer" class="correct-answer">
    <strong>Correct Answer:</strong> {{ question.correct_answer }}
  </div>
</div>
            </div>
          </div>

          <div v-if="currentExam.attachments?.length" class="attachments-section">
            <h4>Attachments</h4>
            <div class="attachments-list">
              <div v-for="(attachment, aIndex) in currentExam.attachments" :key="aIndex" class="attachment-view">
                <a :href="getAttachmentUrl(attachment.file_path)" target="_blank" class="attachment-link">
                  {{ attachment.file_name }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

export default {
  name: "Exams",
  data() {
    return {
      exams: [],
      examTypes: [],
      terms: [],
      classes: [],
      subjects: [],
      showModal: false,
      showViewModal: false,
      isEditing: false,
      currentExam: {},
      currentExamId: null,
      form: {
        name: '',
        exam_type_id: '',
        term_id: '',
        class_id: '',
        subject_id: '',
        start_time: '',
        end_time: '',
        duration_minutes: '',
        total_score: 100,
        instructions: '',
        is_published: false,
        questions: [],
        attachments: [],
      },
    };
  },
  async created() {
    await this.fetchExams();
    await this.fetchDropdownData();
  },
  methods: {
    updateCorrectOption(questionIndex, optionIndex) {
    // Reset all is_correct flags to false
    this.form.questions[questionIndex].options.forEach(opt => {
      opt.is_correct = false;
    });
    // Set the selected option to true
    this.form.questions[questionIndex].options[optionIndex].is_correct = true;
  },
    async fetchExams() {
      try {
        const response = await api.get('/exams');
        this.exams = response.data.data;
      } catch (error) {
        console.error('Error fetching exams:', error);
        this.$toast.error('Failed to load exams');
      }
    },
    async fetchDropdownData() {
      try {
        const [typesRes, termsRes, classesRes, subjectsRes] = await Promise.all([
          api.get('/exam-types'),
          api.get('/terms'),
          api.get('/classes'),
          api.get('/subjects'),
        ]);
        
        this.examTypes = typesRes.data.data;
        this.terms = termsRes.data.data;
        this.classes = classesRes.data;
        this.subjects = subjectsRes.data.data;
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        this.$toast.error('Failed to load dropdown data');
      }
    },
    openCreateModal() {
      this.isEditing = false;
      this.currentExamId = null;
      this.resetForm();
      this.showModal = true;
    },
    editExam(exam) {
  this.isEditing = true;
  this.currentExamId = exam.id;
  
  // Format the exam data for the form
  this.form = {
    name: exam.name,
    exam_type_id: exam.exam_type_id,
    term_id: exam.term_id,
    class_id: exam.class_id,
    subject_id: exam.subject_id,
    start_time: exam.start_time ? this.formatDateTimeForInput(exam.start_time) : '',
    end_time: exam.end_time ? this.formatDateTimeForInput(exam.end_time) : '',
    duration_minutes: exam.duration_minutes,
    total_score: exam.total_score,
    instructions: exam.instructions,
    is_published: exam.is_published,
    questions: exam.questions.map(question => ({
      id: question.id, // Make sure to include the question ID
      question_text: question.question_text,
      type: question.type,
      points: question.points,
      correct_answer: question.correct_answer || '',
      correct_option: question.options ? question.options.findIndex(opt => opt.is_correct) : null,
      options: question.options ? question.options.map(option => ({
        id: option.id, // Include option ID as well
        option_text: option.option_text,
        is_correct: option.is_correct
      })) : []
    })),
    attachments: exam.attachments.map(attachment => ({
      file_name: attachment.file_name,
      file_path: attachment.file_path,
      id: attachment.id
    }))
  };
  
  this.showModal = true;
},
    viewExam(exam) {
      this.currentExam = exam;
      this.showViewModal = true;
    },
    closeViewModal() {
      this.showViewModal = false;
      this.currentExam = {};
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        name: '',
        exam_type_id: '',
        term_id: '',
        class_id: '',
        subject_id: '',
        start_time: '',
        end_time: '',
        duration_minutes: '',
        total_score: 100,
        instructions: '',
        is_published: false,
        questions: [],
        attachments: [],
      };
    },
    addQuestion() {
      this.form.questions.push({
        question_text: '',
        type: 'multiple_choice',
        points: 1,
        correct_option: null,
        options: []
      });
    },
    removeQuestion(index) {
      this.form.questions.splice(index, 1);
    },
    // Update your addOption method to include is_correct
    addOption(questionIndex) {
    if (!this.form.questions[questionIndex].options) {
      this.$set(this.form.questions[questionIndex], 'options', []);
    }
    this.form.questions[questionIndex].options.push({
      option_text: '',
      is_correct: false
    });
  },
    removeOption(questionIndex, optionIndex) {
      this.form.questions[questionIndex].options.splice(optionIndex, 1);
      
      // Update correct option index if needed
      const correctOption = this.form.questions[questionIndex].correct_option;
      if (correctOption === optionIndex) {
        this.form.questions[questionIndex].correct_option = null;
      } else if (correctOption > optionIndex) {
        this.form.questions[questionIndex].correct_option = correctOption - 1;
      }
    },
    handleFileUpload(event) {
      const files = event.target.files;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const base64Content = e.target.result.split(',')[1];
          this.form.attachments.push({
            file_name: file.name,
            file_content: base64Content
          });
        };
        
        reader.readAsDataURL(file);
      }
      
      // Reset file input
      event.target.value = '';
    },
    removeAttachment(index) {
      this.form.attachments.splice(index, 1);
    },
    getAttachmentUrl(filePath) {
      return `${process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api'}/storage/${filePath}`;
    },
    formatDateTimeForInput(dateTimeString) {
      if (!dateTimeString) return '';
      const date = new Date(dateTimeString);
      const offset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date.getTime() - offset);
      return localDate.toISOString().slice(0, 16);
    },
    async submitExam() {
  try {
    // Prepare the questions data
    const questions = this.form.questions.map(question => {
      const questionData = {
        id: question.id, // Include question ID
        question_text: question.question_text,
        type: question.type,
        points: question.points,
        correct_answer: question.type === 'essay' ? question.correct_answer : null,
        options: question.type === 'multiple_choice' ? 
          question.options.map(option => ({
            id: option.id, // Include option ID
            option_text: option.option_text,
            is_correct: option.is_correct
          })) : []
      };
      
      return questionData;
    });
    
    const payload = {
      ...this.form,
      questions: questions,
      // Convert datetime strings to ISO format
      start_time: this.form.start_time 
        ? new Date(this.form.start_time).toISOString().replace('T', ' ').replace('.000Z', '')
        : null,
      end_time: this.form.end_time 
        ? new Date(this.form.end_time).toISOString().replace('T', ' ').replace('.000Z', '')
        : null,
    };
    
    if (this.isEditing) {
      await api.put(`/exams/${this.currentExamId}`, payload);
      this.$toast.success('Exam updated successfully');
    } else {
      await api.post('/exams', payload);
      this.$toast.success('Exam created successfully');
    }
    
    await this.fetchExams();
    this.closeModal();
  } catch (error) {
    console.error('Error saving exam:', error);
    this.$toast.error(error.response?.data?.message || 'Failed to save exam');
  }
},
    async deleteExam(id) {
      if (!confirm('Are you sure you want to delete this exam?')) return;
      
      try {
        await api.delete(`/exams/${id}`);
        this.$toast.success('Exam deleted successfully');
        await this.fetchExams();
      } catch (error) {
        console.error('Error deleting exam:', error);
        this.$toast.error('Failed to delete exam');
      }
    },
  },
};
</script>

<style scoped>
.container {
  padding: 2rem;
}

.title {
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: #2c3e50;
}

.filter-bar {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: flex-end;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.modern-table th,
.modern-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.modern-table th {
  background-color: #f8fafc;
  font-weight: 600;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  cursor: pointer;
  font-weight: 500;
  margin-right: 0.5rem;
}

.add-btn {
  background-color: #4299e1;
  color: white;
  border: none;
}

.edit-btn {
  background-color: #38a169;
  color: white;
  border: none;
}

.view-btn {
  background-color: #3182ce;
  color: white;
  border: none;
}

.delete-btn {
  background-color: #e53e3e;
  color: white;
  border: none;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.published {
  background-color: #c6f6d5;
  color: #22543d;
}

.status-badge.draft {
  background-color: #feebc8;
  color: #7b341e;
}

.no-results {
  text-align: center;
  padding: 2rem;
  color: #718096;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #2d3748;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #718096;
}

/* Form styles */
.form-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.form-section h4 {
  margin-bottom: 1rem;
  color: #4a5568;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #4a5568;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  font-size: 1rem;
}

.form-group textarea {
  min-height: 100px;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-row .form-group {
  flex: 1;
}

.form-check {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.form-check input {
  margin-right: 0.5rem;
}

/* Question styles */
.question-list {
  margin-top: 1rem;
}

.question-card {
  background-color: #f8fafc;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.question-header h5 {
  margin: 0;
  font-size: 1rem;
  color: #2d3748;
}

.options-section {
  margin-top: 1rem;
}

.option-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.option-input {
  display: flex;
  align-items: center;
  flex: 1;
}

.option-input input[type="text"] {
  margin-left: 0.5rem;
  flex: 1;
}

.remove-btn {
  background-color: #fed7d7;
  color: #e53e3e;
  border: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  margin-left: 0.5rem;
}

/* Attachments styles */
.attachments-list {
  margin-top: 1rem;
}

.attachment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background-color: #f8fafc;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
}

.file-upload {
  margin-top: 1rem;
}

/* Form actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.cancel-btn {
  background-color: #e2e8f0;
  color: #4a5568;
  border: none;
  margin-right: 0.5rem;
}

.submit-btn {
  background-color: #4299e1;
  color: white;
  border: none;
}

/* View modal styles */
.exam-details {
  margin-bottom: 1.5rem;
}

.detail-row {
  display: flex;
  margin-bottom: 0.5rem;
}

.detail-label {
  font-weight: 500;
  color: #4a5568;
  min-width: 100px;
}

.questions-section {
  margin-bottom: 1.5rem;
}

.question-view {
  background-color: #f8fafc;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.question-text {
  margin-bottom: 0.5rem;
}

.options-view {
  margin-left: 1rem;
}

.option-view {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}

.option-view input {
  margin-right: 0.5rem;
}

.correct-option {
  font-weight: 500;
  color: #22543d;
}

.essay-view {
  margin-left: 1rem;
  color: #718096;
  font-style: italic;
}

.attachments-section {
  margin-top: 1.5rem;
}

.attachment-view {
  margin-bottom: 0.5rem;
}

.attachment-link {
  color: #3182ce;
  text-decoration: none;
}

.attachment-link:hover {
  text-decoration: underline;
}
.correct-option {
  font-weight: bold;
  color: #4CAF50;
}

.correct-answer {
  margin-top: 10px;
  padding: 10px;
  background-color: #112240;
  border-radius: 4px;
  border-left: 3px solid #4CAF50;
}
</style>